/**
 * REDISTRIBUCIÓN SIN APORTE: achicar el rango con el estable que la piscina YA tiene.
 *
 * Cuando el precio sube dentro del rango, la posición va vendiendo activo y acumula estable. Se
 * cierra y se reabre en un rango más estrecho con casi todo en el activo (el estable propio compra
 * el activo) y el borde del estable pegado al precio. Es la herramienta "Redistribución
 * inteligente sin aporte": la fórmula de achicar con aporte = 0, D·R = T.
 *
 * En la cadena es `achicarRango(below, above, 0)`: con la lógica de 2026-10-29, sin aporte y con el
 * precio pegado al borde del estable, el contrato compra el activo con el estable propio y ajusta la
 * banda para salir con el capital EXACTO (sin la reserva). Aquí solo se calcula qué pedir y cuándo
 * compensa (el "precio gatillo").
 *
 * Mismas unidades que `lib/achicar.ts`: raíces crudas, D/T/estable en unidades humanas del estable.
 */
import { planAncho, planAporte, type Entrada, type Plan } from "./achicar";

const raiz = (t: number) => Math.pow(1.0001, t / 2);

/** La posición de hoy, en crudo, para revalorarla a otro precio. */
export type Posicion = {
  L: number;
  lo: number;
  hi: number;
  /** saldo suelto MENOS lo retenido (reserva, gas, ganancia), crudo; puede ser negativo */
  libre0: number;
  libre1: number;
  dec0: number;
  dec1: number;
};

/** token0/token1 crudos de la liquidez L en [lo, hi) con el precio en `tick`. */
function montos(L: number, lo: number, hi: number, tick: number) {
  const s = raiz(tick), a = raiz(lo), b = raiz(hi);
  const x = s >= b ? 0 : L * (1 / Math.max(s, a) - 1 / b);
  const y = s <= a ? 0 : L * (Math.min(s, b) - a);
  return { x, y };
}

/** Lo que valdría HOY la piscina (como `Entrada`) si el precio estuviera en `tick`. */
export function entradaEn(base: Entrada, p: Posicion, tick: number): Entrada {
  const { x, y } = montos(p.L, p.lo, p.hi, tick);
  // `libre` ya descuenta lo retenido y puede ser negativo (la bolsa de gas en la misma moneda).
  const have0 = Math.max(0, x + p.libre0), have1 = Math.max(0, y + p.libre1);
  const sqrtP = raiz(tick), precio = sqrtP * sqrtP;
  const dRef = base.refEsToken0 ? p.dec0 : p.dec1;
  const est = (base.refEsToken0 ? have0 : have1) / 10 ** dRef;
  const act = (base.refEsToken0 ? have1 / precio : have0 * precio) / 10 ** dRef;
  return { ...base, sqrtP, tick, twap: tick, D: est + act, estable: est };
}

/** El rango más estrecho que se paga solo, sin aportar (null: todavía no compensa). */
export function planAhora(e: Entrada, s: number, anchoHoy: number): Plan | null {
  const p = planAporte(e, 0, s, anchoHoy);
  // Solo vale si de verdad estrecha: un rango igual de ancho que el de hoy no mejora nada.
  return p && p.ancho < anchoHoy * 0.97 ? p : null;
}

/**
 * Precio gatillo para una amplitud: el primer tick, en la dirección en que el activo sube, al que
 * esa amplitud ya se paga sola. null si no se alcanza antes de salir del rango de hoy.
 */
export function gatillo(base: Entrada, p: Posicion, ancho: number, s: number): { tick: number; plan: Plan } | null {
  // Con estable token0 el activo sube cuando el tick BAJA: el final es el suelo.
  const fin = base.refEsToken0 ? p.lo : p.hi - 1;
  const ok = (t: number) => {
    const pl = planAncho(entradaEn(base, p, t), ancho, s);
    return pl && pl.aporte <= 0 ? pl : null;
  };
  const hoy = ok(base.tick);
  if (hoy) return { tick: base.tick, plan: hoy };
  if (!ok(fin)) return null;
  // Bisección en ticks: más subida, más estable acumulado, más fácil pagarlo.
  let lejos = base.tick, cerca = fin;
  while (Math.abs(cerca - lejos) > 1) {
    const m = Math.round((lejos + cerca) / 2);
    if (ok(m)) cerca = m; else lejos = m;
  }
  return { tick: cerca, plan: ok(cerca)! };
}

/** Subida del activo (en %) desde el tick de hoy hasta `t`. */
export function subidaHasta(base: Entrada, t: number): number {
  const r = Math.pow(1.0001, t - base.tick);
  return ((base.refEsToken0 ? 1 / r : r) - 1) * 100;
}

/**
 * EL COSTO: cuánto valdría la piscina si, tras redistribuir, el activo CAE `caidaPct`; comparado con
 * no hacer nada. Redistribuir recompra el activo que el rango había vendido, así que en la caída
 * se pierde más. Valores en el estable, humanos.
 */
export function siCae(base: Entrada, p: Posicion, plan: Plan, caidaPct: number) {
  const dt = Math.round(Math.log(1 - caidaPct / 100) / Math.log(1.0001)) * (base.refEsToken0 ? -1 : 1);
  const t = base.tick + dt;
  const sin = entradaEn(base, p, t).D;
  // Liquidez de la piscina nueva: todo D desplegado en [plan.lo, plan.hi) al precio de hoy.
  const dRef = base.refEsToken0 ? p.dec0 : p.dec1;
  const uno = montos(1, plan.lo, plan.hi, base.tick);
  const precioHoy = base.sqrtP * base.sqrtP;
  const valorPorL = (base.refEsToken0 ? uno.x + uno.y / precioHoy : uno.y + uno.x * precioHoy) / 10 ** dRef;
  const L2 = valorPorL > 0 ? base.D / valorPorL : 0;
  const nueva: Posicion = { ...p, L: L2, lo: plan.lo, hi: plan.hi, libre0: 0, libre1: 0 };
  const con = entradaEn(base, nueva, t).D;
  return { sin, con };
}
